import React from 'react'
import { ProductType } from './ProductCardTypes'
import { useSession } from 'src/sdk/session'
import { useProductShelfSelectItemEvent } from 'src/sdk/analytics/hooks/useProductShelfSelectItemEvent'
import { useProductAbTestEvent } from 'src/sdk/analytics/hooks/useProductAbTestEvent'
import { isNewPDP } from 'src/utils/isNewPDP'
import { Link } from 'gatsby'
import { getCookie } from 'src/utils/cookie'
import {
  getCategorie,
  getItemSizeFromProperties,
  getVariantWithSmallestSize,
} from 'src/utils/getProductDetailsInfo'

type ProductNameProps = {
  product: ProductType
  linkProps: {
    to: string
    onClick: () => void
    'data-testid': string
  }
  name: string
  shelfClass: string
  isKit: boolean
  isRelatedProducts?: boolean
}

export default function ProductName({
  product,
  linkProps,
  name,
  shelfClass,
  isKit,
  isRelatedProducts,
}: ProductNameProps) {
  const { person } = useSession()
  const { sendProductShelfSelectItem } = useProductShelfSelectItemEvent()
  const { sendProductAbTestEvent } = useProductAbTestEvent()
  const uuidToUse = getCookie('uuid')

  const sendSelectItemEventToAnalytics = () => {
    if (!product) {
      return
    }

    const productToUse = getVariantWithSmallestSize(product)

    const skuId = productToUse?.sku

    const productIdMatch = product?.slug?.match(/\b[a-z]{2}\d{8}\b/)

    const productId = productIdMatch ? productIdMatch[0].toUpperCase() : ""

    const productName = product?.name

    const productPrice = productToUse?.offers?.offers?.[0]?.price

    const productDiscount = (
      (product?.offers?.offers?.[0]?.listPrice - product?.offers?.offers?.[0]?.price) * 1
    )

    const productType = product?.name?.match(/^\p{L}+/u)?.[0] ?? ""

    const productWeight = product?.name?.match(/(\d+)\s*k/i)?.[0] ?? ""

    const productBrand = product?.brand?.brandName

    const productReference = productToUse?.gtin

    const categories = getCategorie(
      product?.breadcrumbList?.itemListElement.map((category) => category),
      productName
    )

    const productCollection =
      String(
        product?.isVariantOf?.additionalProperty.find(
          (property) => property.name.toLowerCase() === 'coleção'
        )?.value ?? ''
      ) || ''

    const productMaterial =
      String(
        product?.isVariantOf?.additionalProperty.find(
          (property) => property.name.toLowerCase() === 'material'
        )?.value ?? ''
      ) || ''

    const productSize = getItemSizeFromProperties(
      product?.isVariantOf?.additionalProperty,
      productToUse?.additionalProperty
    )

    const productStone =
      String(
        product?.isVariantOf?.additionalProperty.find(
          (property) =>
            property.name.toLowerCase() === 'pedras'.toLowerCase()
        )?.value ?? ''
      ) || ''

    const productAudience =
      String(
        product?.isVariantOf?.additionalProperty.find(
          (property) =>
            property.name.toLowerCase() === 'sugestão'.toLowerCase()
        )?.value ?? ''
      ) || ''

    const eventData = {
      sku_id: skuId,
      item_id: productId,
      item_name: productName,
      item_price: productPrice,
      item_type: productType,
      item_weight: productWeight,
      item_brand: productBrand,
      item_collection: productCollection,
      item_reference: productReference,
      item_material: productMaterial,
      item_category: categories[0] ?? "",
      item_category2: categories[1] ?? "",
      item_category3: categories[2] ?? "",
      item_category4: categories[3] ?? "",
      item_category5: categories[4] ?? "",
      item_category6: categories[5] ?? "",
      item_currency: 'BRL',
      item_size: productSize ?? "",
      item_stone: productStone,
      item_audience: productAudience,
      quantity: 1,
      discount: productDiscount,
      user_logged: !!person?.id,
      user_id: person?.id ?? null,
      uuid: uuidToUse,
    }

    sendProductShelfSelectItem(eventData)
  }

  const sendSelectItemAbTestEventToAnalytics = (productName: string) => {
    sendProductAbTestEvent({
      pdp_version: isNewPDP ? 'versao_2' : 'versao_1',
      action: 'click_product',
      component: 'produtos',
      position_interaction: 'meio',
      element_name: productName,
      section: 'produtos_relacionados',
      user_id: person?.id ?? null,
      user_logged: !!person?.id,
      uuid: uuidToUse,
    })
  }

  const handleSaveProductIdInSessionStorage = () => {
    sessionStorage.setItem('clickedProductId', product.id)
  }

  if (isKit) {
    return (
      <a
        href={linkProps.to}
        title={name}
        className={`${shelfClass}__name`}
        onClick={() => {
          sendSelectItemEventToAnalytics()
          handleSaveProductIdInSessionStorage()

          if (isRelatedProducts) {
            sendSelectItemAbTestEventToAnalytics(name)
          }
        }}
      >
        <h3 className={`${shelfClass}__name-product`}>{name}</h3>
      </a>
    )
  }

  return (
    <Link
      {...linkProps}
      title={name}
      className={`${shelfClass}__name`}
      onClick={() => {
        sendSelectItemEventToAnalytics()
        handleSaveProductIdInSessionStorage()

        if (isRelatedProducts) {
          sendSelectItemAbTestEventToAnalytics(name)
        }
      }}
    >
      <h3 className={`${shelfClass}__name-product`}>{name}</h3>
    </Link>
  )
}
